import { useTranslation } from 'react-i18next'
import { useCurrentLang } from '@/shared/i18n/use-current-lang'
import { localizeInternalPath } from '@/shared/i18n/localized-path'
import { trackClickTelegram, trackClickX } from '@/shared/analytics'
import { TELEGRAM_CHANNEL_URL, X_PROFILE_URL } from '@/shared/constants'
import { getResourcesIndexPath } from '@/features/resources/content/resources'
import { cn } from '@/shared/lib/utils'
import { isPublicPageHidden } from '@/shared/seo/hidden-public-pages'
import { publicRouteHref } from '@/shared/seo/seo-config'

type AppFooterProps = {
  className?: string
  /** Pages for audiences outside Russia (Laplace x GTM) leave it out. */
  hideTelegram?: boolean
}

export function AppFooter({ className, hideTelegram = false }: AppFooterProps) {
  const { t } = useTranslation('common')
  const lang = useCurrentLang()
  const year = new Date().getFullYear()

  return (
    <footer className={cn('hidden shrink-0 border-t py-3 sm:block', className)}>
      <div className="mx-auto flex max-w-7xl flex-col items-center justify-center gap-2 px-4 text-sm text-muted-foreground sm:flex-row sm:gap-4">
        <span>{t('copyright', { year })}</span>
        <nav className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1">
          {isPublicPageHidden('resources') ? null : (
            <a
              href={getResourcesIndexPath(lang)}
              className="transition-colors hover:text-foreground"
            >
              {t('blog')}
            </a>
          )}
          <a
            href={publicRouteHref('terms', lang)}
            className="transition-colors hover:text-foreground"
          >
            {t('termsOfService')}
          </a>
          <a
            href={publicRouteHref('privacy', lang)}
            className="transition-colors hover:text-foreground"
          >
            {t('privacyPolicy')}
          </a>
          <a
            href={publicRouteHref('refund', lang)}
            className="transition-colors hover:text-foreground"
          >
            {t('refundPolicy')}
          </a>
          {isPublicPageHidden('trust') ? null : (
            <a
              href={localizeInternalPath('/trust', lang)}
              className="transition-colors hover:text-foreground"
            >
              {t('trustSecurity')}
            </a>
          )}
          {hideTelegram ? null : (
            <a
              href={TELEGRAM_CHANNEL_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="transition-colors hover:text-foreground"
              onClick={() =>
                trackClickTelegram({ source: 'footer', language: lang })
              }
            >
              {t('telegram')}
            </a>
          )}
          <a
            href={X_PROFILE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="transition-colors hover:text-foreground"
            onClick={() => trackClickX({ source: 'footer', language: lang })}
          >
            {t('x')}
          </a>
        </nav>
      </div>
    </footer>
  )
}
